<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('productPriceSchema.pageTitle') }}</h1>
      <ts-breadcrumb :items="[
        { text: $t('home'), href: '/' },
        {
          text: $t('productPriceSchema.pageTitle'),
          active: true
        }
      ]" />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <ts-button outline color="info" @click.prevent="addNew">
            {{ $t('add_new') }}
          </ts-button>
          <div class="d-flex ts-items-center ts-space-x-3">
            <Input search v-model="search" :placeholder="$t('productPriceSchema.search')" :style="width"
              @on-focus="() => (width = 'width: 500px')" @on-blur="() => (width = 'width: 300px')" />
            <ts-filter ref="filter">
              <div class="row ts-mt-4">
                <div class="col-md-12">
                  <label>{{ $t('productPriceSchema.category') }}</label>
                  <a-select v-model="category_id" mode="multiple" class="ts-w-full" placeholder="All"
                    :options="productCategories" @change="onChangeCategory()"></a-select>
                </div>
              </div>
              <div class="row ts-mt-4">
                <div class="col-md-12">
                  <label>{{ $t('productPriceSchema.productGroup') }}</label>
                  <a-select v-model="pro_group_id" mode="multiple" class="ts-w-full" placeholder="All"
                    :options="productGroups" @change="fetchData()"></a-select>
                </div>
              </div>
            </ts-filter>
          </div>
        </div>
      </ts-card-wrapper>
      <div class="overflow-auto">
        <Table :columns="columns" :data="resources" size="small" :loading="loading" highlight-row stripe
          max-height="700">
          <template slot-scope="{ row }" slot="action">
            <a href="#" v-tooltip="$t('edit')" class="text-primary mr-2" @click.prevent="edit(row)">
              <i class="fas fa-edit"></i>
            </a>
            <a-popconfirm placement="leftTop" :title="$t('are_you_sure_to_delete')" :ok-text="$t('yes')"
              :cancel-text="$t('no')" @confirm="deleteRecord(row)">
              <a href="#" :disabled="row._deleting">
                <i class="fas fa-circle-notch spin" v-if="row._deleting"></i>
                <i class="fa fa-trash text-red" v-else></i>
              </a>
            </a-popconfirm>
          </template>
        </Table>
        <div class="d-flex justify-content-between align-items-center p-15">
          <ts-total-list v-model="pagination" />
          <ts-pagination v-model="pagination" @navigate="fetchData"></ts-pagination>
        </div>
        <a-modal v-if="showForm" v-model="showForm" draggable sticky scrollable :mask="false" :footer="null"
          :z-index="1022" :title="$t('productPriceSchema.pageTitle')">
          <from-action ref="form_action" @cancel="clearEdit" @fetchData="fetchData" />
        </a-modal>
      </div>
    </ts-card>
  </div>
</template>
<script>
import { mapActions, mapState } from 'vuex'
import FromAction from './edit.vue'
import { debounce, includes } from 'lodash'

export default {
  name: 'product-price-schema',
  components: {
    FromAction
  },
  data() {
    return {
      loading: false,
      showForm: false,
      width: 'width: 300px',
      categories: [],
      groups: [],
      model: {}
    }
  },
  computed: {
    ...mapState('restaurantSale/productPriceSchema', ['resources', 'pagination', 'formViewDatas']),
    search: {
      get() {
        return this.$store.state.restaurantSale.productPriceSchema.search
      },
      set(newValue) {
        this.$store.commit(
          'restaurantSale/productPriceSchema/SET_SEARCH',
          newValue
        )
        this.$store.commit(
          'restaurantSale/productPriceSchema/RESET_CURRENT_PAGE'
        )
      }
    },
    category_id: {
      get() {
        return this.$store.state.restaurantSale.productPriceSchema.category_id
      },
      set(newValue) {
        this.$store.commit(
          'restaurantSale/productPriceSchema/SET_CATEGORY_ID',
          newValue
        )
      }
    },
    pro_group_id: {
      get() {
        return this.$store.state.restaurantSale.productPriceSchema.pro_group_id
      },
      set(newValue) {
        this.$store.commit(
          'restaurantSale/productPriceSchema/SET_GROUP_ID',
          newValue
        )
      }
    },
    productCategories() {
      if (this.formViewDatas['productCategories']) {
        return this.formViewDatas['productCategories'].map(el => ({
          value: el.category_id,
          label: el.category_name_en
        }))
      }
      return []
    },
    productGroups() {
      if (this.formViewDatas['productGroups']) {
        if (this.category_id.length > 0) {
          return this.formViewDatas['productGroups']
            .filter(
              p =>
                includes(
                  this.category_id, p.category_id
                )
            )
            .map(el => ({
              value: el.pro_group_id,
              label: el.pro_group_name_en
            }))
        }
        return this.formViewDatas['productGroups'].map(el => ({
          value: el.pro_group_id,
          label: el.pro_group_name_en
        }))
      }
      return []
    },
    columns() {
      return [
        {
          title: this.$t('productPriceSchema.branch'),
          key: 'branch_name_en',
          width: 130
        },
        {
          title: this.$t('productPriceSchema.productGroup'),
          key: 'pro_group_name_en',
          width: 130
        },
        {
          title: this.$t('productPriceSchema.barcode'),
          key: 'barcode',
          width: 140
        },
        {
          title: this.$t('productPriceSchema.productNameEn'),
          key: 'product_name_en',
          width: 190
        },
        {
          title: this.$t('productPriceSchema.productNameKh'),
          key: 'product_name_kh',
          width: 190
        },
        {
          title: this.$t('productPriceSchema.tableType'),
          key: 'guest_level_name_en',
          width: 150,
          align: 'center'
        },
        {
          title: this.$t('productPriceSchema.incrementType'),
          key: 'increment_type',
          width: 150,
          align: 'center'
        },
        {
          title: this.$t('productPriceSchema.incrementAmount'),
          key: 'increment_amount',
          width: 160,
          align: 'center'
        },
        {
          title: this.$t('productPriceSchema.maxPersons'),
          key: 'max_person',
          width: 160,
          align: 'center'
        },
        {
          title: this.$t('productPriceSchema.serviceLength'),
          key: 'service_length',
          width: 160,
          align: 'center'
        },
        {
          title: this.$t('productPriceSchema.createdBy'),
          key: 'created_by_name',
          width: 130,
        },
        {
          title: this.$t('productPriceSchema.createdDate'),
          key: 'created_date',
          width: 170,
        },
        {
          title: this.$t('actions'),
          key: 'action',
          slot: 'action',
          fixed: 'right',
          align: 'center',
          width: 94
        }
      ]
    }
  },
  created() { },
  methods: {
    ...mapActions('restaurantSale/productPriceSchema', ['getFormViewModel']),
    async getPreData() {
      await this.getFormViewModel({ index: undefined, params: {} })
    },
    onChangeCategory(value) {
      this.pro_group_id = []
      this.fetchData()
    },
    addNew() {
      this.$router.push({ name: 'product-price-schema-create' })
    },
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('restaurantSale/productPriceSchema/fetch', attributes)
        .catch(error => {
          this.loading = false
          this.notice({ type: 'error', text: error.message })
        })
        .finally(() => {
          this.loading = false
        })
    },
    edit(record) {
      this.showForm = true
      this.$store.commit(
        'restaurantSale/productPriceSchema/SET_EDIT_DATA',
        record
      )
    },
    clearEdit() {
      this.showForm = false
      this.$store.commit('restaurantSale/productPriceSchema/SET_EDIT_DATA', {})
    },
    deleteRecord(record) {
      record._deleting = true
      this.$store
        .dispatch('restaurantSale/productPriceSchema/destroy', record.id)
        .then(response => {
          this.$toasted.success(response.message)
          this.fetchData()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          record._deleting = false
        })
    },
    notice(not) {
      this.$Notice[not.type]({
        title: 'PRODUCT PRICE SCHEMA',
        desc: not.text
      })
    }
  },
  watch: {
    search: debounce(function (search) {
      this.fetchData({ search })
    }, 500)
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.getPreData()
      vm.fetchData()
    })
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('restaurantSale/productPriceSchema/RESET_STATE')
    next()
  }
}
</script>
